/**
 * OTA照合 (/ota-check) — 2026-07-13
 *
 * OTA 各社の予約データ (CSV) とシステム側の予約を突き合わせて差分を検出する。
 * 対応OTA: じゃらん / OKIREN / レンナビ (2026-08-16 に OKIREN・レンナビ追加)
 *
 * 各セクション共通の動作:
 *  1. OTA管理画面からDLしたCSVをアップロード (Shift-JIS / UTF-8 自動判定)
 *  2. 予約番号 / ステータス / 貸出日時 等を抽出 (列名は OTA ごとのアダプタで吸収)
 *  3. CSV の日付範囲 ±3日 を元にシステム側予約を fetchDispatchReservations で取得
 *  4. 差分を2種類抽出して表示
 *     A. OTA=有効 かつ システムに contact_code の一致予約が無い
 *     B. OTA=有効 かつ システム=cancelled/nonreserved
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
    TableContainer, Button, Chip, Alert, LinearProgress, Collapse, IconButton, Link, Tooltip,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import dayjs from 'dayjs';
import DashboardSidebar from '../components/DashboardSidebar';
import { fetchDispatchReservations } from '../api/dispatchService';
import {
    OtaRow, decodeFile, isSystemCancelled,
    extractJalanRows, extractOkirenRows, extractRennaviRows,
} from './otaCheck/extract';

interface OtaConfig {
    key: string;
    label: string;
    /** アップロードボタン等のアクセントカラー */
    color: string;
    hoverColor: string;
    softBg: string;
    /** システム側 contact_media がこのOTAか */
    isMedia: (media: string) => boolean;
    /** CSV本文 → 正規化行。必須列が無ければ throw する */
    extract: (text: string) => OtaRow[];
    /** 有効扱いのステータス説明（画面のキャプション用） */
    activeLabel: string;
}

const OTA_CONFIGS: OtaConfig[] = [
    {
        key: 'jalan',
        label: 'じゃらん',
        color: '#dc2626',
        hoverColor: '#b91c1c',
        softBg: '#fef2f2',
        isMedia: (m) => m === 'ja' || m === 'jalan',
        extract: extractJalanRows,
        activeLabel: '予約成立',
    },
    {
        key: 'okiren',
        label: 'OKIREN',
        color: '#0891b2',
        hoverColor: '#0e7490',
        softBg: '#ecfeff',
        isMedia: (m) => m === 'okiren',
        extract: extractOkirenRows,
        activeLabel: '予約確定 / 返却済',
    },
    {
        key: 'rennavi',
        label: 'レンナビ',
        color: '#7c3aed',
        hoverColor: '#6d28d9',
        softBg: '#f5f3ff',
        isMedia: (m) => m === 'レンナビ' || m === 'rennavi',
        extract: extractRennaviRows,
        activeLabel: '予約',
    },
];

// --- OTA 1社分のセクション ----------------------------------------------------
const OtaSection: React.FC<{ config: OtaConfig; defaultOpen?: boolean }> = ({ config, defaultOpen = false }) => {
    const [file, setFile] = useState<File | null>(null);
    const [otaRows, setOtaRows] = useState<OtaRow[]>([]);
    const [systemReservations, setSystemReservations] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [open, setOpen] = useState(defaultOpen);
    const [uploadedAt, setUploadedAt] = useState<string | null>(null);
    const [dateRangeInfo, setDateRangeInfo] = useState<{ start: string; end: string } | null>(null);

    const handleFileSelect = useCallback(async (f: File) => {
        setFile(f);
        setOtaRows([]);
        setSystemReservations([]);
        setError(null);
        setIsLoading(true);
        setUploadedAt(null);
        setDateRangeInfo(null);
        try {
            const text = await decodeFile(f);
            const rows = config.extract(text);
            if (rows.length === 0) {
                throw new Error('CSVから予約データを抽出できませんでした');
            }
            setOtaRows(rows);

            // 日付範囲を決定
            const parsed = rows.map(r => dayjs(r.startDate)).filter(d => d.isValid());
            if (parsed.length === 0) {
                throw new Error('貸出日時をパースできませんでした（例: 2026/03/16 10:30 形式）');
            }
            const minDate = parsed.reduce((a, b) => a.isBefore(b) ? a : b).subtract(3, 'day');
            const maxDate = parsed.reduce((a, b) => a.isAfter(b) ? a : b).add(3, 'day');
            const startStr = minDate.format('YYYY-MM-DD');
            const endStr = maxDate.format('YYYY-MM-DD');
            setDateRangeInfo({ start: startStr, end: endStr });

            const sys = await fetchDispatchReservations({ start_date: startStr, end_date: endStr });
            setSystemReservations(sys || []);
            setUploadedAt(dayjs().format('YYYY-MM-DD HH:mm:ss'));
        } catch (e: any) {
            console.error(`[OtaCheck:${config.key}] CSV読み込みエラー`, e);
            setError(e?.message || String(e));
        } finally {
            setIsLoading(false);
        }
    }, [config]);

    // 差分抽出
    const diffs = useMemo(() => {
        if (otaRows.length === 0) return null;

        // システム側の当該OTA予約を code -> reservation でマップ
        // - 判定は contact_code の完全一致
        // - contact_media は件数カウントのみに使用 (媒体不明でも一致は救う)
        const systemByCode = new Map<string, any>();
        let systemMediaCount = 0;
        systemReservations.forEach(r => {
            const code = String(r.contact_code || '').trim();
            if (!code) return;
            const media = String(r.detail?.contact_media || r.contact_media || '').toLowerCase();
            if (config.isMedia(media)) systemMediaCount++;
            // 同じ code が複数あった場合は「アクティブを優先」
            const existing = systemByCode.get(code);
            if (!existing) { systemByCode.set(code, r); return; }
            if (isSystemCancelled(existing) && !isSystemCancelled(r)) systemByCode.set(code, r);
        });

        const missingInSystem: OtaRow[] = [];
        const cancelMismatch: { ota: OtaRow; system: any }[] = [];
        const statusCounts = new Map<string, number>();
        let activeCount = 0;
        let cancelCount = 0;
        let matchedCount = 0;

        otaRows.forEach(row => {
            statusCounts.set(row.status || '(空)', (statusCounts.get(row.status || '(空)') || 0) + 1);
            if (row.isCancelled) { cancelCount++; return; }
            activeCount++;
            const sys = systemByCode.get(row.reservationNo);
            if (!sys) {
                missingInSystem.push(row);
                return;
            }
            matchedCount++;
            if (isSystemCancelled(sys)) {
                cancelMismatch.push({ ota: row, system: sys });
            }
        });

        return {
            missingInSystem,
            cancelMismatch,
            total: otaRows.length,
            activeCount,
            cancelCount,
            matchedCount,
            systemMediaCount,
            statusCounts: Array.from(statusCounts.entries()).sort((a, b) => b[1] - a[1]),
        };
    }, [otaRows, systemReservations, config]);

    return (
        <Paper sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', mb: 3 }}>
            <Box
                sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: open ? '1px solid #e2e8f0' : 'none', cursor: 'pointer' }}
                onClick={() => setOpen(!open)}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CompareArrowsIcon sx={{ color: config.color }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>{config.label}</Typography>
                    {file && (
                        <Chip size="small" label={file.name} sx={{ ml: 1, maxWidth: 300 }} />
                    )}
                    {uploadedAt && (
                        <Typography variant="caption" sx={{ color: '#94a3b8', ml: 1 }}>
                            読み込み: {uploadedAt}
                        </Typography>
                    )}
                    {diffs && (
                        <>
                            <Chip size="small" color="error" label={`A ${diffs.missingInSystem.length}`} sx={{ ml: 1 }} />
                            <Chip size="small" color="warning" label={`B ${diffs.cancelMismatch.length}`} />
                        </>
                    )}
                </Box>
                <IconButton size="small">
                    {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>

            <Collapse in={open}>
                <Box sx={{ p: 3 }}>
                    <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Button
                            component="label"
                            variant="outlined"
                            startIcon={<UploadFileIcon />}
                            disabled={isLoading}
                            sx={{ borderColor: config.color, color: config.color, '&:hover': { borderColor: config.hoverColor, bgcolor: config.softBg } }}
                        >
                            {config.label}CSVをアップロード
                            <input
                                type="file"
                                hidden
                                accept=".csv,text/csv,application/csv"
                                onClick={(e) => { (e.target as HTMLInputElement).value = ''; }}
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (f) handleFileSelect(f);
                                }}
                            />
                        </Button>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                            Shift-JIS または UTF-8 のCSVに対応
                        </Typography>
                    </Box>

                    {isLoading && <LinearProgress sx={{ mb: 2 }} />}
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    {diffs && (
                        <>
                            {/* サマリ */}
                            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f8fafc' }}>
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1 }}>
                                    集計サマリ
                                    {dateRangeInfo && (
                                        <span style={{ marginLeft: 12 }}>
                                            照合期間: {dateRangeInfo.start} 〜 {dateRangeInfo.end}
                                        </span>
                                    )}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    <Chip label={`CSV総件数: ${diffs.total}`} size="small" />
                                    <Chip label={`有効 (${config.activeLabel}): ${diffs.activeCount}`} size="small" color="primary" variant="outlined" />
                                    <Chip label={`キャンセル等: ${diffs.cancelCount}`} size="small" variant="outlined" />
                                    <Chip label={`システム側${config.label}予約: ${diffs.systemMediaCount}`} size="small" variant="outlined" />
                                    <Chip label={`マッチ: ${diffs.matchedCount}`} size="small" color="success" />
                                    <Chip label={`A. 未登録: ${diffs.missingInSystem.length}`} size="small" color="error" />
                                    <Chip label={`B. キャンセル不整合: ${diffs.cancelMismatch.length}`} size="small" color="warning" />
                                </Box>
                                {/* 想定外のステータス値を握りつぶさないよう内訳を必ず出す */}
                                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 1 }}>
                                    CSVステータス内訳: {diffs.statusCounts.map(([s, n]) => `${s}=${n}`).join(' / ')}
                                </Typography>
                            </Paper>

                            {/* A. OTAに予約あり、システム未登録 */}
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: '#dc2626' }}>
                                    A. {config.label}に予約あり、システム未登録 ({diffs.missingInSystem.length}件)
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1 }}>
                                    {config.label}側「{config.activeLabel}」だが、システム側に <code>contact_code</code> が一致する予約が存在しない
                                </Typography>
                                {diffs.missingInSystem.length === 0 ? (
                                    <Alert severity="success">差分なし</Alert>
                                ) : (
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow sx={{ '& th': { bgcolor: '#fee2e2', fontWeight: 700 } }}>
                                                    <TableCell>予約番号</TableCell>
                                                    <TableCell>ステータス</TableCell>
                                                    <TableCell>予約者</TableCell>
                                                    <TableCell>貸出日時</TableCell>
                                                    <TableCell>返却日時</TableCell>
                                                    <TableCell align="right">合計金額</TableCell>
                                                    <TableCell>車両クラス</TableCell>
                                                    <TableCell>申込日</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {diffs.missingInSystem.map((r) => (
                                                    <TableRow key={r.reservationNo} hover>
                                                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.reservationNo}</TableCell>
                                                        <TableCell>{r.status}</TableCell>
                                                        <TableCell>{r.customerName || '-'}</TableCell>
                                                        <TableCell>{r.startDate}</TableCell>
                                                        <TableCell>{r.endDate}</TableCell>
                                                        <TableCell align="right">{r.totalPrice}</TableCell>
                                                        <TableCell>
                                                            <Tooltip title={r.plan} arrow>
                                                                <span>{r.vehicleClass}</span>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell>{r.applyDate}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Box>

                            {/* B. システムでキャンセル、OTAはキャンセルなし */}
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: '#f59e0b' }}>
                                    B. システムでキャンセル、{config.label}はキャンセルなし ({diffs.cancelMismatch.length}件)
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1 }}>
                                    {config.label}側「{config.activeLabel}」だがシステム側が <code>cancelled</code> / <code>nonreserved</code>
                                </Typography>
                                {diffs.cancelMismatch.length === 0 ? (
                                    <Alert severity="success">差分なし</Alert>
                                ) : (
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow sx={{ '& th': { bgcolor: '#fef3c7', fontWeight: 700 } }}>
                                                    <TableCell>予約番号</TableCell>
                                                    <TableCell>予約者</TableCell>
                                                    <TableCell>{config.label}貸出日時</TableCell>
                                                    <TableCell>{config.label}ステータス</TableCell>
                                                    <TableCell>システムステータス</TableCell>
                                                    <TableCell>システム貸出日時</TableCell>
                                                    <TableCell>予約詳細</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {diffs.cancelMismatch.map(({ ota, system }) => (
                                                    <TableRow key={ota.reservationNo} hover>
                                                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{ota.reservationNo}</TableCell>
                                                        <TableCell>{ota.customerName || '-'}</TableCell>
                                                        <TableCell>{ota.startDate}</TableCell>
                                                        <TableCell>{ota.status}</TableCell>
                                                        <TableCell>
                                                            <Chip label={String(system.status || system.status_code || '不明')} size="small" color="warning" variant="outlined" />
                                                        </TableCell>
                                                        <TableCell>
                                                            {system.start_date ? dayjs(system.start_date).format('YYYY/MM/DD HH:mm') : '-'}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Link href={`/reservations?res=${system.id}`} target="_blank" rel="noopener">
                                                                #{system.id}
                                                            </Link>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Box>
                        </>
                    )}
                </Box>
            </Collapse>
        </Paper>
    );
};

const OtaCheck: React.FC = () => {
    useEffect(() => { document.title = 'OTA照合 | レンタカー予約カレンダー'; }, []);

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
            <DashboardSidebar />

            <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, md: 4 }, pt: { xs: 7, md: 4 }, overflow: 'auto' }}>
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b', mb: 0.5 }}>
                        OTA照合
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                        OTA各社のCSVとシステムの予約を突き合わせて差分を検出します
                    </Typography>
                </Box>

                {OTA_CONFIGS.map((c, i) => (
                    <OtaSection key={c.key} config={c} defaultOpen={i === 0} />
                ))}
            </Box>
        </Box>
    );
};

export default OtaCheck;
